import {
  CombinedStepForTree,
  VerificationTemplateVersionMap,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  InquiryTemplateModuleAttributes,
  ModuleConnectionType,
  ModuleWorkflowVersion,
} from 'dashboard/models/inquiry-template-module';
import {
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepIntegration,
  InquiryTemplateVersionStepLoop,
  InquiryTemplateVersionStepUi,
  InquiryTemplateVersionStepVerifyWithPersona,
} from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { FieldSchema } from 'lib/fields';
import type { StepPreview } from 'lib/interfaces/StepsConfig';

export enum ScreenFlowStepType {
  Screen = 'screen',
  Module = 'module',
  Actions = 'actions',
  Branching = 'branching',
  Connector = 'connector',
  PastedScreen = 'pasted_screen',
  Integration = 'integration',
}

export type ScreenStepConfig =
  | InquiryTemplateVersionCombinedStep
  | InquiryTemplateVersionStepIntegration
  | InquiryTemplateVersionStepVerifyWithPersona
  | InquiryTemplateVersionStepUi
  | InquiryTemplateVersionStepLoop;

export type FlowTreeData = {
  steps: InquiryTemplateVersionStep[];
  stepsPreview: StepPreview[];
  workflowVersionMap: WorkflowVersionMap;
};

export type FlowTreeFormValues = Partial<{
  [K in keyof FlowTreeData]: FlowTreeData[K] | null;
}> & {
  // TEMP - TODO (eric): remove when flow tree update logic
  // is refactored to consistently return full map
  updatedWorkflowVersion?: WorkflowVersion | null;
  vtmplVersionUpdates?: {
    versionMap: VerificationTemplateVersionMap;
    // verification template redux update is only
    // necessary when adding a new VT/VTV via modules
    verificationTemplates?: VerificationTemplate[];
  } | null;
  fieldSchema?: FieldSchema[] | null;
  themeAttributes?: ThemeAttributes | null;
};

export type AddStepConfig = {
  prevStep: CombinedStepForTree;
  nextStep: CombinedStepForTree | null | undefined;
  connectingStepName?: string;
  branchIndex?: number;
};

export type DeleteStepConfig = {
  prevStep: CombinedStepForTree | undefined;
  currentStep: CombinedStepForTree;
  nextStep: CombinedStepForTree | undefined;
  branchIndex?: number;
};

export type NewStep = {
  stepConfig: ScreenStepConfig;
  stepPreview: StepPreview;
  presentInTree?: boolean;
};

/**
 * Specify which transition updates to skip when
 * adding a new step to the flow tree.
 *
 * @param omitNeighborBack don't update the sequential next step's `back-step` config
 * @param omitNeighborNext don't update the sequential prev step's `to-step` config
 * @param omitSelfNext don't update the current step's `to-step` config
 *
 * These are most often used when inserting modules, as we update the first and final screen steps
 * in a module to connect the transitions to the existing step and directly append the rest, which
 * are already correctly connected to the first/final steps.
 */
export type OmitUpdateConfig = {
  omitNeighborBack?: boolean;
  omitNeighborNext?: boolean;
  omitSelfNext?: boolean;
};

export type UpdatedStepsPreviewsWorkflows = {
  updatedSteps?: InquiryTemplateVersionStep[] | null;
  updatedStepsPreview?: StepPreview[] | null;
  updatedWorkflowVersion?: WorkflowVersion | null;
};

export type UpdatedStepsPreviewsWorkflowMap = Omit<
  UpdatedStepsPreviewsWorkflows,
  'updatedWorkflowVersion'
> & {
  updatedWorkflowMap?: WorkflowVersionMap | null;
};

export type FlowTreeModuleAttributes = Omit<
  InquiryTemplateModuleAttributes,
  'field-schemas' | 'verification-template-versions'
>;

export type NewModuleOutgoing = {
  type: ModuleConnectionType;
  object: NewStep | ModuleWorkflowVersion;
};

export type NewModuleConfig = {
  incomingConnection: NewStep;
  moduleSteps: ScreenStepConfig[];
  moduleStepPreviews: StepPreview[];
  moduleWorkflowVersions: WorkflowVersion[];
  outgoingConnection?: NewModuleOutgoing;
  isTerminalModule: boolean;
  isSingleObject: boolean;
  sharedIncomingOutgoingConnection: boolean;
};

export interface NodeWrapperAttributes {
  dataMarketingId: string;
  dataName: string;
  dataTest: string;
  title: string;
}
